/**
 * Sheet / modal primitive (design system, FOUNDATION — v2).
 * Bottom sheet on mobile (spring-up + grabber), centered dialog ≥700px.
 * Escape + backdrop close, focus moves in on open, body scroll locks.
 * Safety flows (S-30/S-35…) reuse this with their own dead-serious content —
 * the chrome carries no humor. Same class + role contract as v1.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { sheetBackdropVariants, sheetPanelVariants } from "./motion.js";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  /** Set false for consent-critical sheets that must be answered explicitly. */
  dismissable?: boolean;
}

export function Sheet({ open, onClose, title, children, dismissable = true }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissable) onClose();
    };
    document.addEventListener("keydown", onKey);
    ref.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, dismissable]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="sheet-backdrop"
          variants={sheetBackdropVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          onClick={(e) => {
            if (dismissable && e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            ref={ref}
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            variants={sheetPanelVariants}
          >
            <div className="sheet__grab" aria-hidden="true" />
            {title && <h2 className="sheet__title">{title}</h2>}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
